import type { ReactNode } from "react"
import {
  Package,
  Tags,
  Layers,
  Wrench,
  Boxes,
  Zap,
  Info,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react"

interface AjudaProps {
  // shell não é necessário — página estática de documentação.
  shell?: unknown
}

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <section className="rounded-lg border border-border bg-card/40 p-5">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
        <span className="text-primary">{icon}</span>
        {title}
      </h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  )
}

function Role({ name, desc }: { name: string; desc: string }) {
  return (
    <div className="flex gap-2">
      <span className="min-w-36 shrink-0 font-medium text-foreground">{name}</span>
      <span>{desc}</span>
    </div>
  )
}

export function Ajuda(_props: AjudaProps) {
  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Ajuda — como gerir o simulador</h1>
        <p className="text-sm text-muted-foreground">
          O simulador gera propostas fotovoltaicas automaticamente. Esta página explica de onde vêm
          os produtos, o que tens de configurar e como tudo se liga.
        </p>
      </div>

      <Section icon={<Package className="h-5 w-5" />} title="De onde vêm os produtos">
        <p>
          Cada produto tem duas origens de informação, que não se misturam:
        </p>
        <ul className="ml-4 list-disc space-y-2">
          <li>
            <b className="text-foreground">Do CRM / Odoo (não editas aqui):</b> nome, SKU, preço de
            venda, preço de custo e marca. Isto é sincronizado automaticamente a partir do Odoo. O{" "}
            <b className="text-foreground">SKU é o identificador principal</b> — é por ele que o
            sistema sabe que produto é.
          </li>
          <li>
            <b className="text-foreground">Do simulador (editas em “Mapeamentos”):</b> a que grupo o
            produto pertence, se está ativo no simulador, e as suas características técnicas (potência,
            fase, capacidade, etc.). Isto vive na app e{" "}
            <b className="text-foreground">nunca é apagado pela sincronização do Odoo</b>.
          </li>
        </ul>
        <p className="rounded-md bg-muted/50 p-3">
          <Info className="mr-1 inline h-4 w-4" />
          O <b className="text-foreground">Nome público</b> (o que o cliente vê na proposta) edita-se na
          ficha do produto no CRM, no campo <i>“Nome alternativo”</i>. Fica fixo — não muda quando o
          Odoo sincroniza (que às vezes traz nomes trocados).
        </p>
      </Section>

      <Section icon={<Tags className="h-5 w-5" />} title="Grupos (a que serve cada produto)">
        <p>
          No simulador, cada produto pertence a um <b className="text-foreground">grupo</b>. O grupo diz
          ao simulador para que serve o produto. Geres isto em <b className="text-foreground">Mapeamentos</b>,
          escolhendo o grupo nos separadores.
        </p>
        <div className="space-y-1.5 rounded-md border border-border p-3">
          <Role name="Painéis" desc="Os módulos fotovoltaicos." />
          <Role name="Inversores" desc="Convertem a energia dos painéis." />
          <Role name="Baterias" desc="Armazenamento de energia." />
          <Role name="Controlador Bateria" desc="Módulo de gestão da bateria (quando aplicável)." />
          <Role name="Estruturas" desc="Suporte dos painéis (coplanar / triangular) — explode em componentes." />
          <Role name="Contadores" desc="Medição / sensores de energia." />
          <Role name="Carregadores" desc="Carregadores de veículo elétrico." />
          <Role name="Backup" desc="Equipamento de backup / corte automático (ATS, SmartGuard…)." />
          <Role name="Instalação" desc="Custo de instalação base." />
          <Role name="Instalação Backup / Carregador" desc="Custos de instalação extra por distância." />
          <Role name="Mão de Obra" desc="Horas de técnicos." />
          <Role name="Acessórios" desc="Dongles, WiFi, transformadores e afins." />
        </div>
      </Section>

      <Section icon={<Wrench className="h-5 w-5" />} title="O que tens de fazer">
        <p>Em <b className="text-foreground">Mapeamentos</b>, por cada grupo:</p>
        <ul className="ml-4 list-disc space-y-2">
          <li>
            <b className="text-foreground">Ligar um produto ao grupo:</b> usa a barra de pesquisa
            “Adicionar produto” — a partir daí o produto passa a estar disponível no simulador.
          </li>
          <li>
            <b className="text-foreground">Preencher a metadata técnica:</b> as colunas de cada grupo
            (potência, fase, Wp, marcas compatíveis…). É o que permite ao simulador escolher o produto
            certo. Guarda ao sair da célula.
          </li>
          <li>
            <b className="text-foreground">Standby (tirar do simulador):</b> desliga o interruptor{" "}
            <b className="text-foreground">Ativo</b>. O produto sai do simulador mas{" "}
            <b className="text-foreground">não é apagado</b> — voltas a ligar quando quiseres.
          </li>
          <li>
            <b className="text-foreground">Remover do grupo:</b> o ícone do lixo tira o produto{" "}
            <b className="text-foreground">deste grupo</b> (não apaga o produto no CRM).
          </li>
        </ul>
      </Section>

      <Section icon={<Zap className="h-5 w-5" />} title="Metadata técnica — o que preencher por grupo">
        <p>Só é preciso preencher o que o simulador usa para escolher:</p>
        <div className="space-y-1.5 rounded-md border border-border p-3">
          <Role name="Painéis" desc="Wp (potência do painel) · Marcas compatíveis." />
          <Role name="Inversores" desc="Fase (mono/tri) · Potência kW · Marcas compatíveis." />
          <Role name="Baterias" desc="Capacidade Wh · Marcas compatíveis · Potências kVA." />
          <Role name="Carregadores" desc="Fase · Potência kW." />
          <Role name="Contadores" desc="Fase." />
          <Role name="Instalação (+ backup/carregador)" desc="Potências kVA compatíveis." />
        </div>
        <p>
          O simulador escolhe automaticamente com base na <b className="text-foreground">marca</b> que
          o comercial seleciona, na <b className="text-foreground">fase</b> do sistema (mono/tri), na{" "}
          <b className="text-foreground">potência instalada</b> e no <b className="text-foreground">Wp</b> do
          painel. Se a metadata estiver em falta, pode escolher mal ou deixar o grupo vazio.
        </p>
      </Section>

      <Section icon={<Tags className="h-5 w-5" />} title="Marcas compatíveis">
        <p>
          O campo <b className="text-foreground">Marcas compat.</b> é uma lista das marcas ativas. Serve
          para o simulador não misturar marcas — ex.: numa proposta Huawei, não pôr um inversor que só
          é compatível com Growatt.
        </p>
        <ul className="ml-4 list-disc space-y-1">
          <li><b className="text-foreground">Vazio (“Todas”):</b> o produto é compatível com qualquer marca.</li>
          <li><b className="text-foreground">Uma ou mais marcas:</b> o produto só é considerado quando a proposta é dessa marca.</li>
        </ul>
      </Section>

      <Section icon={<Boxes className="h-5 w-5" />} title="Estruturas e componentes (BOM)">
        <p>
          Uma estrutura (Coplanar ou Triangular) aparece como{" "}
          <b className="text-foreground">uma linha</b> na proposta ao cliente, mas por dentro é feita de
          vários <b className="text-foreground">componentes reais</b> (guias, grampos, encaixes, triângulos…).
          Esses componentes vão para o Odoo — para o guia de transporte e gestão de stock.
        </p>
        <p>
          O conjunto de componentes é <b className="text-foreground">diferente conforme o nº de painéis</b>{" "}
          (uma instalação de 3 painéis usa guias diferentes de uma de 6). Por isso, na página Mapeamentos →
          Estruturas, ao expandir uma estrutura escolhes o <b className="text-foreground">nº de painéis</b> e
          editas os componentes e quantidades dessa configuração.
        </p>
        <p className="rounded-md bg-muted/50 p-3">
          <AlertTriangle className="mr-1 inline h-4 w-4 text-amber-500" />
          Se um componente aparecer com um triângulo de aviso, é porque o SKU ainda não existe como produto
          no CRM — o componente conta na lista mas fica sem custo/stock resolvido até criares o produto.
        </p>
      </Section>

      <Section icon={<Layers className="h-5 w-5" />} title="Como o simulador monta o kit">
        <p>Ao criar uma proposta, o simulador (resumidamente):</p>
        <ol className="ml-4 list-decimal space-y-1">
          <li>Escolhe o <b className="text-foreground">painel</b> pela potência (Wp) e calcula a quantidade pela potência pretendida.</li>
          <li>Escolhe o <b className="text-foreground">inversor</b> pela fase e potência instalada.</li>
          <li>Acrescenta <b className="text-foreground">bateria, backup ou carregador</b> se essas opções estiverem ligadas.</li>
          <li>Adiciona <b className="text-foreground">instalação e mão de obra</b> conforme a potência contratada.</li>
          <li>Explode a <b className="text-foreground">estrutura</b> nos componentes do nº de painéis.</li>
        </ol>
        <p>
          Tudo respeita a <b className="text-foreground">marca</b> escolhida e as{" "}
          <b className="text-foreground">margens</b> definidas em Configurações → Marcas Solar.
        </p>
      </Section>

      <Section icon={<CheckCircle2 className="h-5 w-5" />} title="Boas práticas">
        <ul className="ml-4 list-disc space-y-1.5">
          <li>Para tirar um produto do simulador, usa o <b className="text-foreground">Standby</b> em vez de apagar.</li>
          <li>Confirma que o <b className="text-foreground">SKU</b> está no produto certo — o guia de transporte do Odoo depende disso.</li>
          <li>Cura o <b className="text-foreground">Nome público</b> no CRM — é o que o cliente lê e não muda com o sync.</li>
          <li>Preenche a <b className="text-foreground">metadata técnica</b> de novos produtos, senão o simulador pode não os escolher.</li>
        </ul>
      </Section>
    </div>
  )
}
